import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  CaseStage,
  DisclosureStatus,
  OfferOptionalTerm,
  OfferSelectionResponse,
  OffersResponse,
  OfferStatus,
  OfferTermsView,
  PaymentFrequency,
  SelectOfferBody,
} from '@tex/finance-shared'
import { ApiError, apiGet, apiSend } from './api'
import { caseKeys } from './cases'

export const offerKeys = {
  offers: (caseId: string) => ['cases', 'detail', caseId, 'offers'] as const,
}

/** Keys the closing tab owns; selection changes what closing can start from, so both are refreshed together. */
export const closingKeys = {
  closing: (caseId: string) => ['cases', 'detail', caseId, 'closing'] as const,
  funding: (caseId: string) => ['cases', 'detail', caseId, 'funding'] as const,
  fees: (caseId: string) => ['cases', 'detail', caseId, 'fees'] as const,
}

export function useOffers(caseId: string, enabled = true) {
  return useQuery({
    queryKey: offerKeys.offers(caseId),
    queryFn: () => apiGet<OffersResponse>(`/api/v1/cases/${caseId}/offers`),
    enabled,
  })
}

/** `selectOffer`: the buyer's preference (or Tex's record of it). A stale `expected_version` refetches so the next try carries the current one. */
export function useSelectOffer(caseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: SelectOfferBody) => apiSend<OfferSelectionResponse>('POST', `/api/v1/cases/${caseId}/offer-selection`, body),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: offerKeys.offers(caseId) }),
        qc.invalidateQueries({ queryKey: closingKeys.closing(caseId) }),
        qc.invalidateQueries({ queryKey: caseKeys.detail(caseId) }),
        qc.invalidateQueries({ queryKey: ['work-queue'] }),
      ]),
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'version_conflict')
        return Promise.all([
          qc.invalidateQueries({ queryKey: offerKeys.offers(caseId) }),
          qc.invalidateQueries({ queryKey: closingKeys.closing(caseId) }),
          qc.invalidateQueries({ queryKey: caseKeys.detail(caseId) }),
        ])
    },
  })
}

export const PAYMENT_FREQUENCY_LABELS: Record<PaymentFrequency, string> = {
  monthly: 'monthly',
  quarterly: 'quarterly',
  semi_annual: 'every six months',
  annual: 'annually',
  other: 'per the partner’s schedule',
}

export const OFFER_STATUS_LABELS: Record<OfferStatus, string> = {
  active: 'Open',
  expired: 'Expired',
  withdrawn: 'Withdrawn',
  superseded: 'Replaced',
}

export const DISCLOSURE_STATUS_LABELS: Record<DisclosureStatus, string> = {
  not_required: 'Disclosure not required',
  pending: 'Provider disclosure pending',
  provider_delivered: 'Provider disclosure delivered',
}

export const OFFER_TERM_LABELS: Record<OfferOptionalTerm, string> = {
  down_payment: 'Down payment',
  payment_amount: 'Payment',
  rate: 'Rate',
  fees: 'Fees',
  residual: 'Residual / buyout amount',
  total_finance_charge: 'Total finance charge',
  guarantee_requirements: 'Guarantees',
  prepayment_terms: 'Prepayment',
  buyout_terms: 'Buyout terms',
  expires_at: 'Expires',
}

/**
 * The partner's rate exactly as stated: their wording first, else the basis points they gave.
 * Nothing is computed from a factor or a payment (AC14).
 */
export function statedRate(terms: OfferTermsView): string | null {
  if (terms.rate_text) return terms.rate_text
  if (terms.rate_bps === null) return null
  const pct = (terms.rate_bps / 100).toFixed(2)
  return terms.rate_type ? `${pct}% ${terms.rate_type}` : `${pct}%`
}

/** Stages at which offers are worth a card at all: once the file is with partners or beyond. */
export const OFFER_STAGES: readonly CaseStage[] = ['lender_review', 'offer_review', 'closing', 'funded', 'closed']
